import * as React from "react";
import {uuid} from "../types/veboxModels";
import {DeviceSlaveCurrentSettings} from "../ReadAndWrite/data/models/DeviceSlaveSetting";
import {AvailableRegister} from "../ReadAndWrite/data/models/AvailableRegister";
import {
    useDeviceSlaveAvailableRegisters,
    useDeviceSlaveDetails,
    useDeviceSlaveSettings,
    useIsRWSupportedCheck,
    useSettingsUpdatesResults
} from "../ReadAndWrite/data/hooks/ReadAndWriteControllerHooks";
import {SWRResponse} from "swr";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import {isRWSupportedCheckResponse} from "../ReadAndWrite/data/models/isRWSupportedCheck";
import {TAB_NAMES} from "../ReadAndWrite/components/ToolBarTabs";
import {PAGES} from "../ReadAndWrite/pages/MainContentPage";
import {TABLES_NAMES} from "../ReadAndWrite/widgets/CurrentConfigurationTable/types";
import {
    ACCESS_DEVICE_SLAVE_DETAIL_SETTINGS,
    EDIT_DEVICE_SLAVE_DETAIL_SETTINGS,
    userHasPermission
} from "../components/Utils/RolesUtils";
import {VeboxUser} from "./VeboxUser";
import {ThermostatDetails} from "../ReadAndWrite/data/models/ThermostatDetails";
import {CurrentSettingsUpdateResult} from "../ReadAndWrite/data/models/CurrentSettingsUpdateResult";
import {canSeePage} from "../ReadAndWrite/pages/MainContentPage/utils";
import {STATUS_TEXTS} from "../ReadAndWrite/components/StatusChip";

declare var AppTranslations: AppTranslations;

interface ReadAndWriteContextType {
    deviceSlaveSettings: SWRResponse<DeviceSlaveCurrentSettings, Error, any>;
    deviceGuid: uuid
    availableRegisters: SWRResponse<AvailableRegister[], Error, any>
    IsRWSupportedCheck: SWRResponse<isRWSupportedCheckResponse, Error, any>
    deviceSlaveDetails: SWRResponse<ThermostatDetails, Error, any>
    AppTranslations: AppTranslations;
    pages: PAGES[];
    currentPage: PAGES;
    changePage: (page: PAGES, force?: boolean) => void;
    tabs: TAB_NAMES[];
    currentTab: TAB_NAMES;
    changeTab: (tab: TAB_NAMES) => void;
    getTableHeadersName: (name: TABLES_NAMES) => string;
    getPageText: (page: PAGES) => string;
    getTabText: (tab: TAB_NAMES) => string;
    getStatusText: (status: STATUS_TEXTS) => string
    hasPermissionsToSeeSettings: boolean;
    hasPermissionsToEditSettings: boolean;
    changeHistoryFilterText: (text: string) => void;
    historyFilterText: string;
    changeRegisterFilterText: (text: string) => void;
    registerFilterText: string;
}

export const ReadAndWriteContext = React.createContext<ReadAndWriteContextType>({} as any);

interface ReadAndWriteProviderProps {
    deviceGuid: uuid;
}

export const ReadAndWriteProvider: React.FC<ReadAndWriteProviderProps> = ({deviceGuid, children}) => {

    const loggedInUser = VeboxUser.useCurrentUser();
    const pages = [PAGES.INFO, PAGES.SETTINGS];
    const [currentPage, setCurrentPage] = React.useState<PAGES>(PAGES.INFO);
    const tabs = [TAB_NAMES.CURRENT, TAB_NAMES.HISTORY];
    const [currentTab, setCurrentTab] = React.useState<TAB_NAMES>(TAB_NAMES.CURRENT);

    const [registerFilterText, setRegisterFilterText] = React.useState<string>("");
    const [historyFilterText, setHistoryFilterText] = React.useState<string>("");

    const [hasPermissionsToSeeSettings, setHasPermissionsToSeeSettings] = React.useState<boolean>(false);
    const [hasPermissionsToEditSettings, setHasPermissionsToEditSettings] = React.useState<boolean>(false);

    const availableRegisters = useDeviceSlaveAvailableRegisters(deviceGuid);
    const deviceSlaveSettings = useDeviceSlaveSettings(deviceGuid);
    const IsRWSupportedCheck = useIsRWSupportedCheck(deviceGuid);
    const deviceSlaveDetails = useDeviceSlaveDetails(deviceGuid)

    const getTableHeadersName = (name: TABLES_NAMES) => {
        switch (name) {
            case TABLES_NAMES.LABEL:
                return AppTranslations.readAndWrite.label;
            case TABLES_NAMES.NAME:
                return AppTranslations.readAndWrite.name;
            case TABLES_NAMES.DEFAULT:
                return AppTranslations.readAndWrite.defaultValue;
            case TABLES_NAMES.MIN:
                return AppTranslations.readAndWrite.min;
            case TABLES_NAMES.MAX:
                return AppTranslations.readAndWrite.max;
            case TABLES_NAMES.CURRENT:
                return AppTranslations.readAndWrite.current;
            case TABLES_NAMES.NEW:
                return AppTranslations.readAndWrite.newValue;
            case TABLES_NAMES.LAST_UPDATE:
                return AppTranslations.readAndWrite.lastUpdate;
            case TABLES_NAMES.READ:
                return AppTranslations.readAndWrite.read;
            case TABLES_NAMES.STATUS:
                return AppTranslations.readAndWrite.status;
            default:
                return '';
        }
    }


    const getPageText = (page: PAGES): string => {
        switch (page) {
            case PAGES.SETTINGS:
                return AppTranslations.readAndWrite.settingsOptionPanel;
            case PAGES.INFO:
                return AppTranslations.readAndWrite.deviceSlaveSectionInfoTitle;
            case PAGES.UPDATING:
                return "Updating";
            case PAGES.EDITING:
                return "Editing";
            default:
                return '';
        }
    }

    const getTabText = (tab: TAB_NAMES): string => {
        switch (tab) {
            case TAB_NAMES.CURRENT:
                return AppTranslations.readAndWrite.currentConfigurationTab;
            case TAB_NAMES.HISTORY:
                return AppTranslations.readAndWrite.historyConfigurationTab;
            default:
                return '';
        }
    }

    const getStatusText = (status: STATUS_TEXTS): string => {
        switch (status) {
            case STATUS_TEXTS.READING:
                return AppTranslations?.readAndWrite.reading ?? "Reading";
            case STATUS_TEXTS.WRITING:
                return AppTranslations?.readAndWrite.writing ?? "Writing"
            case STATUS_TEXTS.VALIDATING:
                return AppTranslations?.readAndWrite.validating ?? "Validating"
            case STATUS_TEXTS.ERROR:
                return AppTranslations?.readAndWrite.error ?? "Error"
            case STATUS_TEXTS.FAILED:
                return AppTranslations?.readAndWrite.failed ?? "Failed"
            case STATUS_TEXTS.COMPLETED:
            case STATUS_TEXTS.CONCLUDED:
                return AppTranslations?.readAndWrite.completed ?? "Completed"
            case STATUS_TEXTS.IN_PROGRESS:
                return AppTranslations?.readAndWrite.inProgress ?? "In Progress"
            default:
                return "None";
        }
    }

    const changePage = (newPage: PAGES, force?: boolean) => {
        const canSeeNewPage = canSeePage(newPage, hasPermissionsToSeeSettings, hasPermissionsToEditSettings,
            IsRWSupportedCheck?.data?.supported);

        if (force || !canSeeNewPage) {
            setCurrentPage(force ? newPage : PAGES.INFO);
        } else if (newPage === PAGES.SETTINGS && (deviceSlaveSettings?.data?.updating || deviceSlaveSettings?.data?.validating)) {
            setCurrentPage(PAGES.UPDATING);
        } else {
            setCurrentPage(newPage);
        }
    }

    const changeTab = (tab: TAB_NAMES) => {
        setCurrentTab(tab)
    }

    const changeRegisterFilterText = (text: string) => {
        setRegisterFilterText(text);
    }
    const changeHistoryFilterText = (text: string) => {
        setHistoryFilterText(text);
    }

    React.useEffect(() => {
        if (loggedInUser) {
            const canSeeSettings = userHasPermission(loggedInUser, ACCESS_DEVICE_SLAVE_DETAIL_SETTINGS);
            setHasPermissionsToSeeSettings(userHasPermission(loggedInUser, ACCESS_DEVICE_SLAVE_DETAIL_SETTINGS));
            setHasPermissionsToEditSettings(
                canSeeSettings && userHasPermission(loggedInUser, EDIT_DEVICE_SLAVE_DETAIL_SETTINGS));
        }
    }, [loggedInUser])

    return (
        <ReadAndWriteContext.Provider
            value={{
                deviceSlaveSettings,
                deviceGuid,
                availableRegisters,
                IsRWSupportedCheck,
                deviceSlaveDetails,
                AppTranslations,
                pages,
                currentPage,
                changePage,
                tabs,
                currentTab,
                changeTab,
                getTableHeadersName,
                getPageText,
                getTabText,
                getStatusText,
                hasPermissionsToSeeSettings,
                hasPermissionsToEditSettings,
                changeHistoryFilterText,
                historyFilterText,
                changeRegisterFilterText,
                registerFilterText
            }}
        >
            {children}
        </ReadAndWriteContext.Provider>
    );
}
